"use strict";

import _defineProperty from "@babel/runtime-corejs3/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import "core-js/modules/es.promise.js";
import { getFeatureName } from '@autoguru/feature-identification';
import { getDataDogMode, isDataDogEnabled } from '@autoguru/lambda-feature-flags/client';
import { buildLogContext } from '../lib';
import { datadogLogsOptions, datadogOptions, getSamplingConfig } from './agentConfigs';
import { syncMountCriticalFlags } from './featureFlagSync';
const DD_RUM_INIT_KEY = '__AG_DATADOG_RUM_INITIALIZED__';
const DD_RUM_INIT_IN_PROGRESS_KEY = '__AG_DATADOG_RUM_INIT_IN_PROGRESS__';
const DD_RUM_INIT_FAILED_KEY = '__AG_DATADOG_RUM_INIT_FAILED__';
const DD_RUM_SDK_LOAD_FAILED_KEY = '__AG_DATADOG_SDK_LOAD_FAILED__';

// Polling configuration for DataDog RUM SDK availability and initialization
const DD_RUM_LOAD_TIMEOUT_MS = 5000;
const DD_RUM_POLL_INTERVAL_MS = 50;
let datadogInitialized = false;
export const getDataDogRUM = () => {
  var _window$DD_RUM;
  if (!process.__browser__) {
    return null;
  }
  return (_window$DD_RUM = window.DD_RUM) !== null && _window$DD_RUM !== void 0 ? _window$DD_RUM : null;
};
const waitForDataDogRUM = (maxWaitMs = DD_RUM_LOAD_TIMEOUT_MS, intervalMs = DD_RUM_POLL_INTERVAL_MS, signal) => {
  return new Promise(resolve => {
    if (!process.__browser__) {
      resolve(null);
      return;
    }
    if (signal !== null && signal !== void 0 && signal.aborted) {
      resolve(null);
      return;
    }
    if (window.DD_RUM) {
      resolve(window.DD_RUM);
      return;
    }
    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (signal !== null && signal !== void 0 && signal.aborted) {
        clearInterval(checkInterval);
        resolve(null);
        return;
      }
      if (window.DD_RUM) {
        clearInterval(checkInterval);
        resolve(window.DD_RUM);
      } else if (Date.now() - startTime >= maxWaitMs) {
        clearInterval(checkInterval);
        console.warn(`[DataDog] RUM SDK not available after waiting ${maxWaitMs / 1000}s`);
        resolve(null);
      }
    }, intervalMs);
    signal === null || signal === void 0 || signal.addEventListener('abort', () => {
      clearInterval(checkInterval);
      resolve(null);
    });
  });
};

/**
 * Synchronously acquire the init lock using atomic compare-and-set pattern.
 * Returns true if this caller acquired the lock, false if another caller already has it.
 */
const acquireInitLock = () => {
  // If already initialised, no lock needed
  if (window[DD_RUM_INIT_KEY]) {
    return false;
  }

  // If another MFE is currently initialising, don't acquire
  if (window[DD_RUM_INIT_IN_PROGRESS_KEY]) {
    return false;
  }

  // Acquire the lock synchronously
  window[DD_RUM_INIT_IN_PROGRESS_KEY] = true;
  return true;
};

/**
 * Wait for another MFE to complete DataDog initialisation
 */
const waitForInitComplete = (maxWaitMs = DD_RUM_LOAD_TIMEOUT_MS, intervalMs = DD_RUM_POLL_INTERVAL_MS, signal) => {
  return new Promise(resolve => {
    if (signal !== null && signal !== void 0 && signal.aborted) {
      resolve();
      return;
    }

    // If already complete, resolve immediately
    if (window[DD_RUM_INIT_KEY]) {
      resolve();
      return;
    }
    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (signal !== null && signal !== void 0 && signal.aborted) {
        clearInterval(checkInterval);
        resolve();
        return;
      }
      if (window[DD_RUM_INIT_KEY] || Date.now() - startTime >= maxWaitMs) {
        clearInterval(checkInterval);
        resolve();
      }
    }, intervalMs);
    signal === null || signal === void 0 || signal.addEventListener('abort', () => {
      clearInterval(checkInterval);
      resolve();
    });
  });
};
export const initializeDataDogRUM = async customOptions => {
  if (!process.__browser__) {
    return;
  }

  // Early exit if already initialised (module-level flag)
  if (datadogInitialized) {
    return;
  }

  // Check if DataDog is enabled via mount-critical feature flag
  if (!isDataDogEnabled()) {
    if (process.env.NODE_ENV !== "production") {
      console.log('[DataDog] Disabled via feature flag');
    }
    return;
  }

  // Early exit if SDK load failed from CDN (set by HTML generator)
  if (window[DD_RUM_SDK_LOAD_FAILED_KEY]) {
    return;
  }

  // Early exit if init previously failed (permanent failure)
  if (window[DD_RUM_INIT_FAILED_KEY]) {
    return;
  }

  // Early exit if already initialised (cross-MFE window flag)
  if (window[DD_RUM_INIT_KEY]) {
    datadogInitialized = true;
    return;
  }

  // Try to acquire the init lock synchronously (before any async operations)
  const hasLock = acquireInitLock();
  if (!hasLock) {
    // Another MFE is initialising - wait for it to complete
    await waitForInitComplete();
    datadogInitialized = true;
    return;
  }

  // We have the lock - proceed with initialisation
  try {
    var _rum$getInitConfigura, _window$DD_LOGS$getIn, _window$DD_LOGS;
    const rum = await waitForDataDogRUM();
    if (!rum) {
      // SDK not available after timeout - mark as permanently failed
      window[DD_RUM_INIT_FAILED_KEY] = true;
      return;
    }

    // Final safety check: SDK might have been initialised by external source
    if ((_rum$getInitConfigura = rum.getInitConfiguration) !== null && _rum$getInitConfigura !== void 0 && _rum$getInitConfigura.call(rum)) {
      datadogInitialized = true;
      window[DD_RUM_INIT_KEY] = true;
      return;
    }

    // Get portal identity and DataDog mode from feature flags
    const portal = getFeatureName();
    const mode = getDataDogMode();
    const samplingConfig = getSamplingConfig(portal, mode);

    // Perform initialisation with portal-specific, feature-flag-driven sampling
    const options = _objectSpread(_objectSpread(_objectSpread({}, datadogOptions), samplingConfig), customOptions);
    rum.init(options);
    rum.setGlobalContext(buildLogContext());

    // Sync mount-critical feature flags to DataDog context
    syncMountCriticalFlags();

    // Initialize browser-logs for 100% error capture (independent of RUM sampling)
    // Per Datadog: "Front-end logs can be collected at 100% by enabling forwardErrorsToLogs"
    if (window.DD_LOGS && !((_window$DD_LOGS$getIn = (_window$DD_LOGS = window.DD_LOGS).getInitConfiguration) !== null && _window$DD_LOGS$getIn !== void 0 && _window$DD_LOGS$getIn.call(_window$DD_LOGS))) {
      window.DD_LOGS.init(datadogLogsOptions);
      window.DD_LOGS.setGlobalContext(buildLogContext());
    }
    datadogInitialized = true;
    window[DD_RUM_INIT_KEY] = true;
    if (process.env.NODE_ENV !== "production") {
      var _window$DD_LOGS2, _window$DD_LOGS2$getI;
      console.log('[DataDog] RUM initialised with portal-specific config', {
        portal,
        mode,
        sessionSampleRate: options.sessionSampleRate,
        sessionReplaySampleRate: options.sessionReplaySampleRate
      });
      if ((_window$DD_LOGS2 = window.DD_LOGS) !== null && _window$DD_LOGS2 !== void 0 && (_window$DD_LOGS2$getI = _window$DD_LOGS2.getInitConfiguration) !== null && _window$DD_LOGS2$getI !== void 0 && _window$DD_LOGS2$getI.call(_window$DD_LOGS2)) {
        console.log('[DataDog] Logs SDK initialised for 100% error capture');
      }
    }
  } catch (error) {
    // Init threw an error - mark as permanently failed
    window[DD_RUM_INIT_FAILED_KEY] = true;
    if (process.env.NODE_ENV !== "production") {
      console.error('[DataDog] RUM initialisation failed:', error);
    }
  } finally {
    // Always release the in-progress lock
    window[DD_RUM_INIT_IN_PROGRESS_KEY] = false;
  }
};
export const isDataDogInitialized = () => {
  if (process.__browser__) {
    var _window$DD_RUM2, _window$DD_RUM2$getIn;
    return datadogInitialized || window[DD_RUM_INIT_KEY] === true || !!((_window$DD_RUM2 = window.DD_RUM) !== null && _window$DD_RUM2 !== void 0 && (_window$DD_RUM2$getIn = _window$DD_RUM2.getInitConfiguration) !== null && _window$DD_RUM2$getIn !== void 0 && _window$DD_RUM2$getIn.call(_window$DD_RUM2));
  }
  return datadogInitialized;
};
export const __resetDataDogForTesting = () => {
  datadogInitialized = false;
  if (process.__browser__) {
    window.DD_RUM = undefined;
    window[DD_RUM_INIT_KEY] = undefined;
    window[DD_RUM_INIT_IN_PROGRESS_KEY] = undefined;
    window[DD_RUM_INIT_FAILED_KEY] = undefined;
    window[DD_RUM_SDK_LOAD_FAILED_KEY] = undefined;
  }
};